import { useSupportSheet } from "../../components/SupportSheet";
import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  BankIcon,
  Button,
  CashSpecialIcon,
  WalletIcon,
  ExchangeIcon,
  Layout,
  SupportIcon,
  Title,
  WarningIcon,
} from "lola-framework-ui-test";
import { FlagCircle } from "../../components/FlagCircle";
import { QuoteInfo } from "../../components/QuoteInfo";
import { PayoutInfo } from "../../components/PayoutInfo";
import { FooterDetailSummary } from "../../components/FooterDetailSummary";
import { Popup } from "../../components/Popup";
import {
  SelectFundingMethodSlot,
  type FlatFundingPick,
} from "../../components/deferred-origination/SelectFundingMethodSlot";
import { AmountOutOfRangeInfo } from "../../components/AmountOutOfRangeInfo";
import { SelectOriginCountrySlot } from "../../components/send/SelectOriginCountrySlot";
import { SelectDestinationCountrySlot } from "../../components/send/SelectDestinationCountrySlot";
import { DEFERRED_ORIGINATION_ROUTES, TITLE_COLOR } from "../../constants";
import { useGalleryPickerLoading } from "../../hooks/useGalleryPickerLoading";
import { useStore } from "../../store/globalState";
import { formatBankAccountLabel } from "../../utils/bankAccountLabel";
import { useSessionStore } from "../../store/useSessionStore";
import { useDeferredOriginationStore } from "../../store/useDeferredOriginationStore";
import {
  DEFERRED_ORIGINATION_MOCK_FX,
  deferredOriginationEstimateOptions,
  deferredOriginationFundingDetails,
  deferredOriginationSavedCards,
} from "../../gallery/deferredOriginationMock";
import { sendCountries, sendOriginCountries } from "../../gallery/sendMock";
import {
  applyFirstSendSeed,
  isFirstSendWarningVisible,
} from "../../gallery/firstSendMock";
import { FirstSendWarning } from "../../components/first-send/FirstSendWarning";
import { useGalleryFirstSend } from "../../hooks/useGalleryFirstSend";
import {
  FUNDING_METHOD_CATEGORY,
  availableFundingMethods,
  categoryFromFundingMethod,
  optionForFundingMethod,
  type FundingMethod,
} from "../../utils/sendCatalog";
import {
  seedDestinationCountry,
  seedOriginCountry,
  supportedDestinations,
  supportedOrigins,
} from "../../utils/sendCorridors";
import { currencyForCountry } from "../../utils/catalogCountries";
import { amountFormatter } from "../../utils";
import { compareSendToBound } from "../../utils/fundingLimits";
import { paymentType } from "../../interfaces/payment";
import masterCardLogo from "../../assets/master_card_icon.svg";
import visaLogo from "../../assets/visa_color_icon.svg";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";


/**
 * `primaryMesh` is usually a gradient string, which is invalid for `color` and
 * `border-color`. The outlined icon in the rate pill needs a solid value, so it
 * falls back to the gradient's first stop.
 */
const solidStop = (color: string) =>
  color.match(/#[0-9a-f]{3,8}|rgba?\([^)]*\)/i)?.[0] ?? color;

const firstSendCard = deferredOriginationSavedCards[0];

/**
 * Send-origination compose screen (gallery): amount + corridor countries +
 * funding. Confirm stores composeDraft and routes to Recipient.
 */
export const TransferPage = memo(() => {
  const { t } = useTranslation("deferred-origination");
  const { theme, amountState, paymentState, setPaymentState } = useStore();
  const { session } = useSessionStore();
  const { composeDraft, setComposeDraft } = useDeferredOriginationStore();
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("deferred-origination");

  const { amount } = amountState;
  const colorConfig = theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    dropzoneColors,
    bannerHighlightBackground,
    bannerHighlightColor,
    bannerHighlightIconColors,
  } = colorConfig as typeof colorConfig & {
    bannerHighlightBackground?: string;
    bannerHighlightColor?: string;
    bannerHighlightIconColors?: string[];
  };

  const numericAmount = amount ? parseFloat(String(amount).replace(/,/g, "")) : NaN;
  const hasAmount = !Number.isNaN(numericAmount) && numericAmount > 0;

  const corridors = session.data?.config?.corridors ?? [];
  const config = session.data?.config;
  const seededOrigin =
    seedOriginCountry(corridors, config?.defaultCorridor) ??
    supportedOrigins(corridors)[0] ??
    "US";
  const seededDestination =
    seedDestinationCountry(corridors, config?.defaultCorridor) ??
    supportedDestinations(corridors, seededOrigin)[0] ??
    "MX";

  const [originCountry, setOriginCountry] = useState(seededOrigin);
  const [destinationCountry, setDestinationCountry] =
    useState(seededDestination);
  const firstSend = useGalleryFirstSend();
  const [fundingMethod, setFundingMethod] = useState<FundingMethod | null>(
    () => (firstSend ? (firstSendCard.fundingMethod ?? "DEBIT_CARD") : null)
  );
  const hydratedFunding = useRef(Boolean(firstSend));
  const [popup, setPopup] = useState<
    "origin" | "destination" | "funding" | "amountLimit" | null
  >(null);
  const fundingPickerLoading = useGalleryPickerLoading(popup === "funding");
  const [shakeFunding, setShakeFunding] = useState(false);
  const [showExcratePopup, setShowExcratePopup] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const originCodes = useMemo(() => {
    const fromCorridors = supportedOrigins(corridors);
    return fromCorridors.length > 0 ? fromCorridors : [seededOrigin];
  }, [corridors, seededOrigin]);

  const destinationCodes = useMemo(() => {
    const fromCorridors = supportedDestinations(corridors, originCountry);
    return fromCorridors.length > 0 ? fromCorridors : [seededDestination];
  }, [corridors, originCountry, seededDestination]);

  const originCountriesForPicker = useMemo(
    () =>
      sendOriginCountries.filter((c) =>
        originCodes.includes(c.countryCode)
      ),
    [originCodes]
  );

  const destinationCountriesForPicker = useMemo(
    () =>
      sendCountries.filter((c) => destinationCodes.includes(c.countryCode)),
    [destinationCodes]
  );

  const sourceCurrency = useMemo(() => {
    if (
      originCountry === config?.defaultCorridor?.originCountryCode &&
      config?.sourceCurrency
    ) {
      return config.sourceCurrency;
    }
    const match = originCountriesForPicker.find(
      (c) => c.countryCode === originCountry
    );
    return currencyForCountry(match) ?? "USD";
  }, [originCountry, config, originCountriesForPicker]);

  const destinationCurrency = useMemo(() => {
    const match = sendCountries.find((c) => c.countryCode === destinationCountry);
    return (
      currencyForCountry(match) ??
      config?.destinationCurrency ??
      "MXN"
    );
  }, [destinationCountry, config?.destinationCurrency]);

  const fundingMethods = useMemo(
    () => availableFundingMethods(deferredOriginationEstimateOptions),
    []
  );

  const selectedOption = useMemo(() => {
    if (!fundingMethod || !hasAmount) return null;
    return optionForFundingMethod(
      deferredOriginationEstimateOptions,
      fundingMethod
    );
  }, [fundingMethod, hasAmount]);

  const receiveAmount =
    hasAmount && selectedOption
      ? (numericAmount / selectedOption.sendAmount) *
        selectedOption.receiveAmount
      : hasAmount
        ? numericAmount * DEFERRED_ORIGINATION_MOCK_FX
        : 0;
  const totalDebit =
    hasAmount && selectedOption
      ? numericAmount + (selectedOption.customerFee ?? 0)
      : hasAmount
        ? numericAmount
        : null;
  const estimatedRate =
    selectedOption?.customerFxRate ?? DEFERRED_ORIGINATION_MOCK_FX;

  const needsInstrument =
    fundingMethod != null &&
    FUNDING_METHOD_CATEGORY[fundingMethod] === "CARD";
  const hasInstrument =
    Boolean(paymentState.payment?.cardId) || !needsInstrument;

  // Gallery seeds a global VISA mock for other flows. Don't treat that as a
  // Pay-with selection until this screen has a fundingMethod (widget parity).
  useEffect(() => {
    if (hydratedFunding.current) return;
    const seeded = composeDraft?.fundingMethod;
    if (!seeded || !(seeded in FUNDING_METHOD_CATEGORY)) return;
    hydratedFunding.current = true;
    setFundingMethod(seeded as FundingMethod);
  }, [composeDraft?.fundingMethod]);

  useEffect(() => {
    if (!firstSend) return;
    return applyFirstSendSeed({
      payment: {
        cardId: firstSendCard.paymentMethodId,
        type: paymentType.CARD,
        brand: `${(firstSendCard.brand || "").toUpperCase()}-${firstSendCard.last4}`,
      },
    });
  }, [firstSend]);

  const fundingSummary = useMemo(() => {
    if (!fundingMethod) return t("transfer.selectFundingMethod");
    const methodLabel = t(`funding.methods.${fundingMethod}`, {
      defaultValue: fundingMethod,
    });
    if (needsInstrument && !hasInstrument) {
      return t("payment.selectMethod");
    }
    if (paymentState.payment?.brand) {
      return paymentState.payment.brand;
    }
    return methodLabel;
  }, [
    fundingMethod,
    needsInstrument,
    hasInstrument,
    paymentState.payment?.brand,
    t,
  ]);

  const pickIcon = useCallback(
    (method: string | null, brand?: string) => {
      const category = categoryFromFundingMethod(method);
      if (category === "CARD" && brand) {
        let logo: string | null = null;
        if (brand.toUpperCase().includes("VISA")) logo = visaLogo;
        if (brand.toUpperCase().includes("MASTERCARD")) logo = masterCardLogo;
        if (logo) return <img src={logo} alt={`${brand} logo`} />;
      }
      if (category === "ACCOUNT") return <BankIcon size={20} />;
      if (category === "CASH") {
        return <CashSpecialIcon size={20} />;
      }
      return <WalletIcon size={20} />;
    },
    []
  );

  const fundingReady = Boolean(fundingMethod) && hasInstrument;
  const selectedFundingDetails = fundingMethod
    ? deferredOriginationFundingDetails[fundingMethod]
    : undefined;
  const amountBoundVerdict = compareSendToBound(
    hasAmount ? numericAmount : null,
    selectedFundingDetails
  );
  const amountOutOfRange =
    amountBoundVerdict === "below_min" || amountBoundVerdict === "above_max";
  const showFirstSendWarning = isFirstSendWarningVisible({
    sendAmount: hasAmount ? numericAmount : null,
    sourceCurrency,
    fundingCategory:
      fundingMethod && fundingReady
        ? FUNDING_METHOD_CATEGORY[fundingMethod]
        : null,
  });
  const canConfirm = hasAmount && fundingReady && !pricingLoading;
  const blockForPricing =
    hasAmount && fundingReady && (pricingLoading || totalDebit == null);
  const showRainbow = (canConfirm || confirmLoading) && !amountOutOfRange;

  // Gallery mock of pre-quote latency (live deferred uses real preQuoteLoading).
  useEffect(() => {
    if (!hasAmount) {
      setPricingLoading(false);
      return;
    }
    setPricingLoading(true);
    const timer = window.setTimeout(() => setPricingLoading(false), 400);
    return () => window.clearTimeout(timer);
  }, [
    hasAmount,
    numericAmount,
    fundingMethod,
    originCountry,
    destinationCountry,
  ]);

  const nudgeIncompleteCompose = useCallback(() => {
    setShakeFunding(true);
    window.setTimeout(() => setShakeFunding(false), 500);
    const scrollToGap = () => {
      const target =
        document.querySelector(".payout-container .payment-required") ??
        document.querySelector(".payout-container .shake") ??
        document.querySelector(".estimated-quote-section");
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    requestAnimationFrame(scrollToGap);
    window.setTimeout(scrollToGap, 50);
  }, []);

  const clearFunding = useCallback(() => {
    setFundingMethod(null);
    setPaymentState({ payment: null });
  }, [setPaymentState]);

  const handleOriginChange = useCallback(
    (code: string) => {
      if (code !== originCountry) {
        clearFunding();
        const validDestinations = supportedDestinations(corridors, code);
        if (
          validDestinations.length > 0 &&
          !validDestinations.includes(destinationCountry)
        ) {
          const pinned = config?.defaultCorridor?.destinationCountryCode;
          setDestinationCountry(
            pinned && validDestinations.includes(pinned)
              ? pinned
              : validDestinations[0]
          );
        }
      }
      setOriginCountry(code);
      setPopup(null);
    },
    [
      originCountry,
      destinationCountry,
      corridors,
      clearFunding,
      config?.defaultCorridor?.destinationCountryCode,
    ]
  );

  const handleDestinationChange = useCallback(
    (code: string) => {
      if (code !== destinationCountry) clearFunding();
      setDestinationCountry(code);
      setPopup(null);
    },
    [destinationCountry, clearFunding]
  );

  const pickFunding = useCallback(
    (pick: FlatFundingPick) => {
      if (pick.kind === "add") {
        setFundingMethod(pick.method);
        setPaymentState({ payment: null });
        setPopup(null);
        navigate(DEFERRED_ORIGINATION_ROUTES.ADD_CARD);
        return;
      }
      if (pick.kind === "card") {
        const method = pick.card.fundingMethod ?? "DEBIT_CARD";
        setFundingMethod(method);
        setPaymentState({
          payment: {
            cardId: pick.card.paymentMethodId,
            type: paymentType.CARD,
            brand: `${(pick.card.brand || "").toUpperCase()}-${pick.card.last4}`,
          },
        });
        setPopup(null);
        return;
      }
      if (pick.kind === "account") {
        setFundingMethod("ACH");
        setPaymentState({
          payment: {
            cardId: pick.account.paymentMethodId,
            type: paymentType.BANK,
            brand:
              formatBankAccountLabel(
                pick.account.bankName,
                pick.account.maskedAccountNumber,
              ) ?? pick.account.bankName,
          },
        });
        setPopup(null);
        return;
      }
      const method = pick.kind === "cash" ? "CASH_DEPOSIT" : pick.method;
      setFundingMethod(method);
      if (method === "CASH_DEPOSIT") {
        setPaymentState({
          payment: {
            cardId: null,
            type: paymentType.CASH,
            brand: t("payment.cashDeposit"),
          },
        });
      } else {
        setPaymentState({ payment: null });
      }
      setPopup(null);
    },
    [navigate, setPaymentState, t]
  );

  const handleFundingRowClick = () => {
    if (!hasAmount) return;
    setPopup("funding");
  };

  const handleConfirm = useCallback(() => {
    if (confirmLoading) return;
    // Send parity: CTA always pressable; nudge missing amount / Pay with.
    if (!hasAmount || !fundingReady) {
      // Only nudge — pickers open from Pay with, not from the CTA.
      nudgeIncompleteCompose();
      return;
    }
    // Narrow for compose draft: fundingReady implies a method, but TS cannot.
    if (!fundingMethod) return;
    if (amountOutOfRange) {
      setPopup("amountLimit");
      return;
    }
    // Compose complete — wait for mock pricing without implying a missing field.
    if (pricingLoading || totalDebit == null) return;

    setConfirmLoading(true);
    setComposeDraft({
      sourceCountry: originCountry,
      destinationCountry,
      sourceCurrency,
      destinationCurrency,
      sendAmount: numericAmount,
      totalDebit: totalDebit ?? numericAmount,
      fundingMethod,
      capabilityKey: selectedOption?.capabilityKey,
      fundingPaymentMethodId: paymentState.payment?.cardId ?? undefined,
    });
    window.setTimeout(() => {
      navigate(DEFERRED_ORIGINATION_ROUTES.RECIPIENT);
    }, 300);
  }, [
    confirmLoading,
    hasAmount,
    fundingReady,
    pricingLoading,
    fundingMethod,
    amountOutOfRange,
    paymentState.payment?.cardId,
    nudgeIncompleteCompose,
    setComposeDraft,
    originCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    numericAmount,
    totalDebit,
    selectedOption?.capabilityKey,
    navigate,
  ]);

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="scrollable need-help-header"
      >
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => navigate(DEFERRED_ORIGINATION_ROUTES.HOME)}
            onIconClick={openSupport}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <section>
            <Title title={t("transfer.title")} align="left" color={TITLE_COLOR} />
            <section
              className={`estimated-quote-section stacked-quote${
                shakeFunding && !hasAmount ? " shake" : ""
              }`}
            >
              <section className="quote-container">
                <div className="banrural-amounts">
                  <div className="banrural-send-wrap">
                    <QuoteInfo
                      label={t("transfer.youSend")}
                      currency={sourceCurrency}
                      textColor={bodyCopy}
                      mode="input"
                      isActive
                      showSelectorChevron={originCodes.length > 1}
                      onSelect={
                        originCodes.length > 1
                          ? () => setPopup("origin")
                          : undefined
                      }
                      icon={<FlagCircle iso2={originCountry.toLowerCase()} />}
                    />
                    <span
                      className="banrural-swap"
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowExcratePopup(true)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setShowExcratePopup(true);
                        }
                      }}
                      style={{ background: backgroundBtn }}
                    >
                      <ExchangeIcon size={18} colors={[textColorBtn, textColorBtn]} />
                    </span>
                  </div>
                  <QuoteInfo
                    label={t("transfer.theyReceive")}
                    amount={
                      hasAmount
                        ? amountFormatter(receiveAmount, destinationCurrency, {
                            country: "en-US",
                          })
                        : undefined
                    }
                    currency={destinationCurrency}
                    textColor={bodyCopy}
                    mode="info"
                    hideCurrencySymbol={destinationCurrency !== "USD"}
                    showSelectorChevron={destinationCodes.length > 1}
                    onSelect={
                      destinationCodes.length > 1
                        ? () => setPopup("destination")
                        : undefined
                    }
                    icon={
                      <FlagCircle iso2={destinationCountry.toLowerCase()} />
                    }
                    bottomSlot={
                      <section
                        className="banrural-exchange-rate banrural-exchange-rate--alert"
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowExcratePopup(true)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setShowExcratePopup(true);
                          }
                        }}
                        style={
                          {
                            "--bgc": bannerHighlightBackground ?? bodyCopy,
                            "--colorc": solidStop(
                              bannerHighlightIconColors?.[0] ?? title,
                            ),
                            "--fillc": bannerHighlightColor ?? title,
                          } as CSSProperties
                        }
                      >
                        <span className="banrural-exchange-rate-icon">i</span>
                        <span className="banrural-exchange-rate-text">
                          <b>
                            {t("exchange.rate", {
                              from: sourceCurrency,
                              rate: estimatedRate.toFixed(4),
                              to: destinationCurrency,
                            })}
                          </b>
                          <label>{t("exchange.estimatedRate")}</label>
                        </span>
                      </section>
                    }
                  />
                </div>
              </section>
            </section>
            <section className="payout-container">
              <PayoutInfo
                title={t("transfer.payWith")}
                subtitle={fundingSummary}
                icon={pickIcon(fundingMethod, paymentState.payment?.brand)}
                background={backgroundBtn}
                color={textColorBtn}
                locked={!hasAmount}
                onChange={handleFundingRowClick}
                className={`${
                  hasAmount && !fundingReady ? "payment-required" : ""
                } ${shakeFunding && !fundingReady ? "shake" : ""}`.trim()}
              />
              {showFirstSendWarning && firstSend ? (
                <FirstSendWarning copy={firstSend} />
              ) : null}
              <BodyCopy
                className="footer-text transfer-timing-note"
                textColor={TITLE_COLOR}
              >
                <b>{t("transfer.shareableLink")}</b>
                {" "}
                <span
                  className="transfer-timing-note-rest"
                  style={{ color: bodyCopy }}
                >
                  {t("transfer.shareableLinkHint")}
                </span>
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <FooterDetailSummary
              heading={t("transfer.detail")}
              fee={t("transfer.fee", {
                amount: `$${(selectedOption?.customerFee ?? 0).toFixed(2)}`,
                currency: sourceCurrency,
              })}
              total={t("transfer.totalPayment", {
                amount: `$${(totalDebit ?? 0).toFixed(2)}`,
                currency: sourceCurrency,
              })}
              titleColor={TITLE_COLOR}
              textColor={TITLE_COLOR}
            />
            <div
              className={`button-wrapper ${showRainbow ? "rainbow-ready" : ""}`}
            >
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={confirmLoading || blockForPricing}
                onClick={handleConfirm}
              >
                {confirmLoading
                  ? t("transfer.prepare.loading")
                  : blockForPricing
                    ? t("transfer.loadingQuote")
                    : t("common.continue")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>

      <Popup
        visible={popup === "amountLimit" && amountOutOfRange}
        onClose={() => setPopup(null)}
      >
        <AmountOutOfRangeInfo
          ns="deferred-origination"
          kind={amountBoundVerdict === "below_min" ? "below_min" : "above_max"}
          method={t(`funding.methods.${fundingMethod ?? ""}`, {
            defaultValue: fundingMethod ?? "",
          })}
          bound={
            amountBoundVerdict === "below_min"
              ? (selectedFundingDetails?.minAmount ?? 0)
              : (selectedFundingDetails?.maxAmount ?? 0)
          }
          currency={sourceCurrency}
          onClose={() => setPopup(null)}
        />
      </Popup>
      <Popup visible={popup === "origin"} onClose={() => setPopup(null)}>
        <SelectOriginCountrySlot
          ns="deferred-origination"
          countries={originCountriesForPicker}
          selected={originCountry}
          onSelect={handleOriginChange}
          onClose={() => setPopup(null)}
        />
      </Popup>
      <Popup visible={popup === "destination"} onClose={() => setPopup(null)}>
        <SelectDestinationCountrySlot
          ns="deferred-origination"
          countries={destinationCountriesForPicker}
          selected={destinationCountry}
          onSelect={handleDestinationChange}
          onClose={() => setPopup(null)}
        />
      </Popup>
      <Popup visible={popup === "funding"} onClose={() => setPopup(null)}>
        <SelectFundingMethodSlot
          selectedMethod={fundingMethod}
          selectedPaymentMethodId={paymentState.payment?.cardId ?? null}
          available={fundingMethods}
          cards={deferredOriginationSavedCards}
          loading={fundingPickerLoading}
          details={deferredOriginationFundingDetails}
          onPick={pickFunding}
          onClose={() => setPopup(null)}
        />
      </Popup>

      <Popup
        visible={showExcratePopup}
        onClose={() => setShowExcratePopup(false)}
      >
        <section className="qoute-popup">
          <WarningIcon size={60} />
          <BodyCopy textColor={bodyCopy}>
            <Trans
              i18nKey="transfer.quote.disclaimer"
              ns="deferred-origination"
            />
          </BodyCopy>
        </section>
      </Popup>
      {supportSheet}
    </>
  );
});
